import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Calendar, Copy, Cruise, EditProfile, Flights, GredientArw, Guest, Hotel, Location, Rental, Wishlist } from '../../assests/ExportImageIcon'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import { formatDateRange } from './wishlistPersonal/wishlistCard'
import DatePicker from '../../componants/datePicker/DatePicker'
import '../onboarding/AccountSetup.css'
import './wishlist.css'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
            d="M2 4h12M5.5 4V2.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V4m-7 0 .7 8.4a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L12.5 4"
            stroke="#E7000B"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

// record_module -> type badge icon (falls back to the generic wishlist heart
// for modules we don't have a dedicated icon for).
const TYPE_ICONS = {
    flight: Flights,
    hotel: Hotel,
    car: Rental,
    cruise: Cruise,
}

const typeIcon = (recordModule) => {
    const key = String(recordModule || '').toLowerCase().replace(/s$/, '')
    return TYPE_ICONS[key] || Wishlist
}

const titleCase = (value) => value.charAt(0).toUpperCase() + value.slice(1)

const WishlistDetail = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const wishlistId = searchParams.get('id')

    const [wishlist, setWishlist] = useState(null)
    const [loading, setLoading] = useState(true)
    const [activeFilter, setActiveFilter] = useState('all')
    const [deletingId, setDeletingId] = useState(null)
    const [revokingId, setRevokingId] = useState(null)
    const [deletingWishlist, setDeletingWishlist] = useState(false)
    const [editing, setEditing] = useState(false)
    const [editName, setEditName] = useState('')
    const [editDestination, setEditDestination] = useState('')
    const [editDateFrom, setEditDateFrom] = useState('')
    const [editDateTo, setEditDateTo] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)

    useEffect(() => {
        if (!wishlistId) {
            setLoading(false)
            return
        }
        setLoading(true)
        service
            .get(endpoints.wishlists.detail.replace('{wishlist_id}', wishlistId), {}, { silent: true })
            .then(({ data }) => setWishlist(data.data))
            .catch(() => setWishlist(null))
            .finally(() => setLoading(false))
    }, [wishlistId])

    const items = wishlist?.items || []

    const filterTabs = useMemo(() => {
        const counts = {}
        items.forEach((item) => {
            const key = String(item.record_module || 'other').toLowerCase()
            counts[key] = (counts[key] || 0) + 1
        })
        return [
            { key: 'all', label: `All (${items.length})` },
            ...Object.keys(counts).map((key) => ({ key, label: `${titleCase(key)} (${counts[key]})` })),
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wishlist?.items])

    const filteredItems = activeFilter === 'all'
        ? items
        : items.filter((item) => String(item.record_module || '').toLowerCase() === activeFilter)

    const handleCopyShare = () => {
        if (!wishlist?.share_link) return
        navigator.clipboard?.writeText(wishlist.share_link)
        showToast('Share link copied', 'success')
    }

    const handleDeleteItem = (item) => {
        if (!item.can_delete || deletingId) return
        setDeletingId(item.id)
        service
            .delete(endpoints.wishlists.itemDelete.replace('{wishlist_id}', wishlistId).replace('{item_id}', item.id))
            .then(() => {
                setWishlist((prev) => (prev ? {
                    ...prev,
                    items: prev.items.filter((i) => i.id !== item.id),
                    items_count: Math.max(0, (prev.items_count || 1) - 1),
                } : prev))
                // showToast('Item removed', 'success')
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setDeletingId(null))
    }

    const handleRevokeAccess = (accessUser) => {
        if (revokingId) return
        setRevokingId(accessUser.id)
        service
            .delete(endpoints.wishlists.accessDelete.replace('{wishlist_id}', wishlistId).replace('{access_id}', accessUser.id))
            .then(() => {
                setWishlist((prev) => (prev ? {
                    ...prev,
                    accessed_users: (prev.accessed_users || []).filter((u) => u.id !== accessUser.id),
                    members_count: Math.max(0, (prev.members_count || 1) - 1),
                } : prev))
                // showToast('Access removed', 'success')
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setRevokingId(null))
    }

    const handleDeleteWishlist = () => {
        if (deletingWishlist) return
        setDeletingWishlist(true)
        service
            .delete(endpoints.wishlists.delete.replace('{wishlist_id}', wishlistId))
            .then(() => {
                // showToast('Wishlist deleted', 'success')
                navigate('/wishlist', { replace: true })
            })
            .catch(() => {
                // error toast already shown by the service interceptor
                setDeletingWishlist(false)
            })
    }

    const startEdit = () => {
        setEditName(wishlist.name || '')
        setEditDestination(wishlist.destination || '')
        setEditDateFrom(wishlist.date_from || '')
        setEditDateTo(wishlist.date_to || '')
        setEditing(true)
    }

    const handleSaveEdit = () => {
        if (savingEdit) return
        if (!editName.trim()) {
            showToast('Please enter a wishlist name', 'error')
            return
        }
        setSavingEdit(true)
        service
            .put(endpoints.wishlists.update.replace('{wishlist_id}', wishlistId), {
                name: editName.trim(),
                destination: editDestination.trim(),
                date_from: editDateFrom || null,
                date_to: editDateTo || null,
            })
            .then(({ data }) => {
                setWishlist((prev) => (prev ? { ...prev, ...data.data } : data.data))
                setEditing(false)
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSavingEdit(false))
    }

    if (!wishlistId || (!loading && !wishlist)) {
        return (
            <section className='allSearchArea commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='fare-extra-hint'>{wishlistId ? 'Wishlist not found.' : 'No wishlist selected.'}</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/wishlist')}>Back to Wishlists</button>
                </div>
            </section>
        )
    }

    if (loading || !wishlist) {
        return (
            <section className='allSearchArea commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p className='fare-extra-hint'>Loading wishlist...</p>
                </div>
            </section>
        )
    }

    // The default individual wishlist is auto-provisioned per user and isn't
    // meant to be edited or deleted — only collaborative wishlists the user
    // owns get those actions.
    const canManageWishlist = wishlist.is_owner && wishlist.type !== 'individual'

    return (
        <section className='allSearchArea commonTopPadding commonBottomPadding'>
            <div className='container'>
                <div className='row g-4'>
                    <div className='col-md-12'>
                        <div className='commonMaxWidth'>
                            <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                <BackArrow />
                                <span className='bsSectionTitle'>Detail</span>
                            </div>
                        </div>
                    </div>

                    <div className='col-md-12'>
                        <div className='commonMaxWidth bgWhite commonBorder borderRadius20 paddingCommon20'>
                            {editing ? (
                                <>
                                    <div className='bsSectionTitle'>Edit Wishlist</div>
                                    <div className='accountSetup__field mt-3'>
                                        <label>Wishlist Name</label>
                                        <input type='text' value={editName} onChange={(e) => setEditName(e.target.value)} />
                                    </div>
                                    <div className='accountSetup__field mt-3'>
                                        <label>Destination(s)</label>
                                        <input type='text' value={editDestination} onChange={(e) => setEditDestination(e.target.value)} />
                                    </div>
                                    <div className='accountSetup__field mt-3'>
                                        <label>Dates</label>
                                        <div className='createWishlistDates'>
                                            <div className='createWishlistDateField'>
                                                <DatePicker value={editDateFrom} onChange={setEditDateFrom} />
                                            </div>
                                            <div className='createWishlistDateField'>
                                                <DatePicker value={editDateTo} onChange={setEditDateTo} min={editDateFrom || undefined} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='bookingBtnRow mt-4'>
                                        <button type='button' className='commonBtnTransparent' onClick={() => setEditing(false)} disabled={savingEdit}>
                                            Cancel
                                        </button>
                                        <button type='button' className='commonBtn' onClick={handleSaveEdit} disabled={savingEdit}>
                                            {savingEdit ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className='wishlistCollabCard__top'>
                                        <div>
                                            <div className='wishlistCollabCard__title'>{wishlist.name}</div>
                                            <div className='wishlistCollabCard__location'>
                                                <img src={Location} alt="" /> {wishlist.destination || '—'}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                            {canManageWishlist && (
                                                <img
                                                    src={EditProfile}
                                                    alt="Edit wishlist"
                                                    className='wishlistDetail__editIcon'
                                                    onClick={startEdit}
                                                />
                                            )}
                                            {wishlist.share_link && (
                                                <img
                                                    src={Copy}
                                                    alt="Copy share link"
                                                    className='wishlistDetail__editIcon'
                                                    style={{ filter: 'brightness(0)' }}
                                                    onClick={handleCopyShare}
                                                />
                                            )}
                                            {canManageWishlist && (
                                                <button
                                                    type='button'
                                                    className='wishlistCard__removeBtn'
                                                    onClick={handleDeleteWishlist}
                                                    disabled={deletingWishlist}
                                                    aria-label="Delete wishlist"
                                                    title="Delete wishlist"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className='wishlistDetail__createdBy'>
                                        {wishlist.is_owner ? 'Created by you' : <>Created by <span>{wishlist.owner_name}</span></>}
                                    </div>

                                    <div className='bsFareDivider' />

                                    <div className='wishlistCollabCard__bottom' style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                                        <span className='wishlistCollabCard__meta'><img src={Calendar} alt="" /> {formatDateRange(wishlist.date_from, wishlist.date_to)}</span>
                                        {wishlist.members_count > 0 && (
                                            <span className='wishlistCollabCard__meta'>
                                                <img src={Guest} alt="" /> {wishlist.members_count} Member{wishlist.members_count > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {wishlist.accessed_users?.length > 0 && (
                        <div className='col-md-12'>
                            <div className='commonMaxWidth bgWhite commonBorder borderRadius20 paddingCommon20'>
                                <div className='bsSectionTitle'>Shared with</div>
                                <div className='wishlistPage__list mt-3'>
                                    {wishlist.accessed_users.map((user) => (
                                        <div className='wishlistCard commonBorder borderRadius20' key={user.id}>
                                            <div className='wishlistCard__info'>
                                                <div className='hotelCard__name'>{user.name || user.email}</div>
                                                {user.name && <div className='hotelCard__locationNote mt-2'>{user.email}</div>}
                                            </div>
                                            {wishlist.is_owner && (
                                                <button
                                                    type='button'
                                                    className='wishlistCard__removeBtn'
                                                    onClick={() => handleRevokeAccess(user)}
                                                    disabled={revokingId === user.id}
                                                    aria-label="Remove access"
                                                    title="Remove access"
                                                >
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='col-md-12'>
                        <div className='commonMaxWidth'>
                            <div className='commonBookingTab'>
                                {filterTabs.map((tab) => (
                                    <div
                                        key={tab.key}
                                        className={`bookTab ${activeFilter === tab.key ? 'active' : ''}`}
                                        onClick={() => setActiveFilter(tab.key)}
                                    >
                                        {tab.label}
                                    </div>
                                ))}
                            </div>

                            <div className='mt-4 bgWhite commonBorder borderRadius20 paddingCommon20'>
                                <div className='selectItemsCount'>
                                    <span className='selectItemsCount__label'>
                                        {filteredItems.length} {filteredItems.length === 1 ? 'Item' : 'Items'} found
                                    </span>
                                </div>

                                {!filteredItems.length ? (
                                    <p className='fare-extra-hint'>No items saved here yet.</p>
                                ) : (
                                    <div className='wishlistPage__list'>
                                        {filteredItems.map((item) => (
                                            <div className='wishlistCard commonBorder borderRadius20' key={item.id}>
                                                <div className='wishlistCard__media'>
                                                    {item.image_url ? <img src={item.image_url} alt={item.record_name} /> : null}
                                                    <div className='wishlistCard__mediaBadges'>
                                                        <span className='wishlistCard__typeBadge'>
                                                            <img src={typeIcon(item.record_module)} alt="" />
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className='wishlistCard__info'>
                                                    <div className='hotelCard__name'>{item.record_name || 'Saved item'}</div>
                                                    {wishlist.members_count > 0 && item.added_by_email && (
                                                        <div className='hotelCard__locationNote mt-2'>Added by {item.added_by_email}</div>
                                                    )}
                                                </div>

                                                {item.can_delete && (
                                                    <button
                                                        type='button'
                                                        className='wishlistCard__removeBtn'
                                                        onClick={() => handleDeleteItem(item)}
                                                        disabled={deletingId === item.id}
                                                        aria-label="Remove item"
                                                        title="Remove item"
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className='wishlistItinerary mt-4'>
                                <span className='wishlistItinerary__icon'><img src={Location} alt="" /></span>
                                <div className='wishlistItinerary__text'>
                                    <div className='wishlistItinerary__title'>Plan Your Perfect Trip</div>
                                    <div className='wishlistItinerary__desc'>Organize your saved items into a complete trip itinerary with timelines, budgets, and collaborators</div>
                                </div>
                                <button type='button' className='wishlistItinerary__btn' onClick={() => navigate('/create-wishlist')}>
                                    Create New Trip <img src={GredientArw} alt="" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default WishlistDetail
